import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { CalendarIcon, X } from 'lucide-react'
import {
  Button,
  Calendar,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/primitives'
import { formatDate } from '@/shared/infra/format'
import { cn } from '@/shared/ui/cn'

type DatePickerProps = {
  id?: string
  label?: string
  value: string
  onChange: (next: string) => void
  onBlur?: () => void
  placeholder?: string
  minDate?: string
  maxDate?: string
  clearable?: boolean
  disabled?: boolean
  required?: boolean
  error?: string
  ariaLabel?: string
  className?: string
  captionLayout?: React.ComponentProps<typeof Calendar>['captionLayout']
  startMonth?: string
  endMonth?: string
}

function formatDisplay(iso: string): string | null {
  return formatDate(iso) || null
}

function parseDate(iso?: string): Date | undefined {
  if (!iso) return undefined
  const parsed = parseISO(iso)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

function buildDisabled(minDate?: string, maxDate?: string) {
  const min = parseDate(minDate)
  const max = parseDate(maxDate)
  if (min && max) return { before: min, after: max }
  if (min) return { before: min }
  if (max) return { after: max }
  return undefined
}

function DatePicker({
  id,
  label,
  value,
  onChange,
  onBlur,
  placeholder = 'Pick a date',
  minDate,
  maxDate,
  clearable,
  disabled,
  required,
  error,
  ariaLabel,
  className,
  captionLayout,
  startMonth,
  endMonth,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const displayValue = formatDisplay(value)
  const selectedDate = parseDate(value)
  const disabledMatcher = buildDisabled(minDate, maxDate)
  const showClear = clearable && !!value && !disabled

  const handleSelect = (date: Date | undefined) => {
    if (!date) return
    onChange(format(date, 'yyyy-MM-dd'))
    setOpen(false)
  }

  const handleClear = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onChange('')
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next && onBlur) onBlur()
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label ? (
        <Label htmlFor={id}>
          {label}
          {required ? ' *' : null}
        </Label>
      ) : null}
      <div className="relative">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              disabled={disabled}
              aria-label={ariaLabel ?? label}
              aria-invalid={error ? true : undefined}
              aria-describedby={error && id ? `${id}-error` : undefined}
              className={cn(
                'w-full justify-start bg-card text-left font-normal',
                showClear && 'pr-9',
                !displayValue && 'text-muted-foreground',
              )}
            >
              <CalendarIcon className="mr-2 size-4 text-muted-foreground" />
              <span className="flex-1 truncate">
                {displayValue ?? placeholder}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0 shadow-xl ring-1 ring-border-strong"
            align="start"
          >
            <Calendar
              mode="single"
              fixedWeeks
              showOutsideDays
              captionLayout={captionLayout}
              startMonth={parseDate(startMonth)}
              endMonth={parseDate(endMonth)}
              selected={selectedDate}
              defaultMonth={selectedDate}
              onSelect={handleSelect}
              disabled={disabledMatcher}
              autoFocus
            />
          </PopoverContent>
        </Popover>
        {showClear ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear date"
            onClick={handleClear}
            className="absolute right-2 top-1/2 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>
      {error ? (
        <p
          id={id ? `${id}-error` : undefined}
          role="alert"
          className="text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}

export { DatePicker }
export type { DatePickerProps }
